import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { TableToolbarContainer } from '../styles';
import TableToolbar from './tableToolbar';

interface TableToolbarPortalProps {
  editor: Editor | null;
}

const TableToolbarPortal: React.FC<TableToolbarPortalProps> = ({ editor }) => {
  const [currentCell, setCurrentCell] = useState<HTMLElement | null>(null);
  const [tableToolbarPosition, setTableToolbarPosition] = useState({ top: 0, left: 0 });
  const [tableWrapper, setTableWrapper] = useState<HTMLElement | null>(null);
  const currentCellRef = useRef<HTMLElement | null>(null);
  const tableWrapperRef = useRef<HTMLElement | null>(null);

  const updateToolbarPosition = useCallback(() => {
    const tableWrapper = tableWrapperRef.current;
    if (!tableWrapper || !editor) return;

    const tableWrapperRect = tableWrapper.getBoundingClientRect();
    const editorRect = editor.view.dom.getBoundingClientRect();

    const topPosition = tableWrapperRect.top - editorRect.top - 35;
    const adjustedTopPosition = topPosition < 0 ? 0 : topPosition;

    setTableToolbarPosition({
      top: adjustedTopPosition,
      left: tableWrapperRect.left - editorRect.left + tableWrapperRect.width / 2 - 195.5
    });
  }, [editor]);

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = ({ editor }: { editor: Editor }) => {
      const { from } = editor.state.selection;
      const domAtPos = editor.view.domAtPos(from);
      const node = domAtPos.node;

      let cell: HTMLElement | null = null;
      let wrapper: HTMLElement | null = null;

      if (node instanceof HTMLElement) {
        cell = node.closest('td, th') as HTMLElement | null;
        if (cell) {
          wrapper = cell.closest('.tableWrapper') as HTMLElement | null;
        }
      } else if (node?.parentElement) {
        cell = node.parentElement.closest('td, th');
        if (cell) {
          wrapper = cell.closest('.tableWrapper') as HTMLElement | null;
        }
      }

      if (cell && editor.isActive('table') && wrapper) {
        currentCellRef.current = cell;
        tableWrapperRef.current = wrapper;
        setCurrentCell(cell);
        setTableWrapper(wrapper);
        updateToolbarPosition();
      } else {
        currentCellRef.current = null;
        tableWrapperRef.current = null;
        setCurrentCell(null);
        setTableWrapper(null);
      }
    };

    editor.on('selectionUpdate', handleSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor, updateToolbarPosition]);

  useEffect(() => {
    if (!editor) return;

    const editorContainer = editor.view.dom;
    const handleScroll = () => {
      if (currentCellRef.current && tableWrapperRef.current) {
        updateToolbarPosition();
      }
    };

    editorContainer.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      editorContainer.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [editor, updateToolbarPosition]);

  useEffect(() => {
    if (!tableWrapper) return;

    const resizeObserver = new ResizeObserver(() => {
      if (currentCellRef.current && tableWrapperRef.current) {
        updateToolbarPosition();
      }
    });

    resizeObserver.observe(tableWrapper);

    return () => {
      resizeObserver.disconnect();
    };
  }, [tableWrapper, updateToolbarPosition]);

  if (!currentCell || !editor) return null;

  return (
    <TableToolbarContainer
      top={tableToolbarPosition.top}
      left={tableToolbarPosition.left}
      isMobile={window.innerWidth <= 449}>
      <TableToolbar editor={editor} />
    </TableToolbarContainer>
  );
};

export default TableToolbarPortal;
